import { useEffect, useRef, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query'
import { Photo } from '../components/Photo'
import { SubmissionSheet } from '../components/SubmissionSheet'
import { useToast } from '../lib/toast'
import { Pips, Stepper } from '../components/Votes'
import { Banner, Button, Chip, EmptyState, PageTitle, Spinner } from '../components/ui'
import { cx } from '../lib/cx'
import { type Submission, api, call, messageOf } from '../lib/api'
import { ballotQuery, leagueQuery, roundQuery, roundsQuery } from '../lib/queries'
import { countdown, deadlineLabel, useNow } from '../lib/time'
import { votes } from '../design/tokens'

export const Route = createFileRoute('/l/$slug/vote')({ component: Vote })

function Vote() {
  const { slug } = Route.useParams()
  const { data: league } = useSuspenseQuery(leagueQuery(slug))
  const rounds = useQuery(roundsQuery(slug))
  const now = useNow()
  const voting = rounds.data?.find((r) => r.status === 'voting')
  const round = useQuery({ ...roundQuery(slug, voting?.number ?? 0), enabled: !!voting })
  const [index, setIndex] = useState(0)

  if (rounds.isPending || (voting && round.isPending)) return <div className="grid place-items-center py-16 text-muted"><Spinner /></div>

  if (!voting || !round.data) {
    const open = league.current_round
    return (
      <>
        <PageTitle eyebrow="Voting">Nothing to vote on yet</PageTitle>
        {open?.status === 'submitting' ? (
          <Banner label="Voting opens in" detail={`When round ${open.number} stops taking posts, ${deadlineLabel(open.submit_close_at)}`} value={countdown(open.submit_close_at, now)} />
        ) : (
          <EmptyState title="Between rounds">Check back when the next round closes.</EmptyState>
        )}
        <p className="text-[14.5px] text-muted">
          Everyone gets {league.vote_budget} {votes(league.vote_budget)} per category, to split however they like. You can't vote for your own posts.
        </p>
      </>
    )
  }

  const cats = round.data.categories ?? []
  const cat = cats[Math.min(index, cats.length - 1)]
  return (
    <>
      <PageTitle eyebrow={`Round ${round.data.number}, category ${index + 1} of ${cats.length}`} aside={<Chip tone="live">{countdown(round.data.vote_close_at, now)}</Chip>}>
        {cat.name}
      </PageTitle>
      <div className="flex gap-1.5" role="tablist" aria-label="Categories">
        {cats.map((c, i) => (
          <button key={c.id} type="button" role="tab" aria-selected={i === index} onClick={() => setIndex(i)} className="min-w-0 flex-1">
            <span className={cx('block h-1.5 rounded-full', i === index ? 'bg-ink' : c.my_votes_spent > 0 ? 'bg-nose' : 'bg-line')} />
            <span className={cx('mt-1 block truncate text-[11.5px] font-semibold', i === index ? 'text-ink' : 'text-muted')}>{c.name}</span>
          </button>
        ))}
      </div>
      <BallotPanel
        key={cat.id}
        slug={slug}
        roundNumber={round.data.number}
        categoryId={cat.id}
        next={index < cats.length - 1 ? { name: cats[index + 1].name, go: () => { setIndex(index + 1); window.scrollTo({ top: 0 }) } } : undefined}
      />
    </>
  )
}

function BallotPanel({ slug, roundNumber, categoryId, next }: { slug: string; roundNumber: number; categoryId: number; next?: { name: string; go: () => void } }) {
  const qc = useQueryClient()
  const toast = useToast()
  const ballot = useQuery(ballotQuery(categoryId))
  // Local allocations are the source of truth while voting, so every tap is
  // instant. They're saved a moment after the last tap.
  const [points, setPoints] = useState<Record<number, number> | null>(null)
  const [open, setOpen] = useState<Submission | null>(null)
  const dirty = useRef(false)

  useEffect(() => {
    if (ballot.data && points === null) {
      setPoints(Object.fromEntries((ballot.data.entries ?? []).map((e) => [e.submission.id, e.points])))
    }
  }, [ballot.data, points])

  const save = useMutation({
    mutationFn: (p: Record<number, number>) =>
      call(api.PUT('/categories/{id}/ballot', {
        params: { path: { id: categoryId } },
        body: { allocations: Object.entries(p).filter(([, n]) => n > 0).map(([id, n]) => ({ submission_id: Number(id), points: n })) },
      })),
    onSuccess: (data) => {
      qc.setQueryData(ballotQuery(categoryId).queryKey, data)
      qc.invalidateQueries({ queryKey: ['league', slug, 'round', roundNumber] })
    },
    onError: (e) => {
      toast(messageOf(e), 'bad')
      setPoints(null) // fall back to what the server has
      qc.invalidateQueries({ queryKey: ballotQuery(categoryId).queryKey })
    },
  })

  useEffect(() => {
    if (!dirty.current || points === null) return
    const id = setTimeout(() => {
      dirty.current = false
      save.mutate(points)
    }, 600)
    return () => clearTimeout(id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [points])

  if (ballot.isPending || points === null) return <div className="grid place-items-center py-10 text-muted"><Spinner /></div>
  if (ballot.isError) return <EmptyState title="Couldn't load your ballot">{messageOf(ballot.error)}</EmptyState>

  const b = ballot.data
  const entries = b.entries ?? []
  const spent = Object.values(points).reduce((a, n) => a + n, 0)
  const left = b.budget - spent
  const change = (id: number, n: number) => {
    dirty.current = true
    setPoints((p) => ({ ...(p ?? {}), [id]: n }))
  }

  if (entries.length === 0) {
    return (
      <>
        <EmptyState title="Nothing to vote on here">Only your own posts are in this category, and those are off limits.</EmptyState>
        {next && <Button variant="ink" block onClick={next.go}>Next: {next.name}</Button>}
      </>
    )
  }

  return (
    <>
      <div className="sticky top-[env(safe-area-inset-top,0px)] z-20 -mx-4 bg-paper/95 px-4 py-2 backdrop-blur">
        <div className="flex items-center justify-between gap-3 rounded-card border border-line bg-surface px-3.5 py-2.5">
          {/* Live, so each tap of a stepper is answered with what's left: its own count remounts and isn't heard. */}
          <span className="text-[15px]" aria-live="polite" aria-atomic="true">
            <b className="font-score text-[22px] font-bold tabular-nums">{left}</b> {votes(left)} left
          </span>
          <span className="flex items-center gap-2.5">
            <span className="text-[12px] text-muted" aria-live="polite">{save.isPending ? 'Saving…' : save.isSuccess && !dirty.current ? 'Saved' : ''}</span>
            <Pips filled={spent} total={b.budget} />
          </span>
        </div>
      </div>
      <ul className="grid gap-2.5">
        {entries.map(({ submission: s }) => {
          const mine = points[s.id] ?? 0
          const cap = b.max_per_submission ?? b.budget
          return (
            <li key={s.id} className="grid grid-cols-[92px_minmax(0,1fr)] items-center gap-3 rounded-card border border-line bg-surface p-2">
              <button type="button" onClick={() => setOpen(s)} aria-label={`Look at ${s.pet_name}`}>
                <Photo media={s.media} alt="" square className="!rounded-field" />
              </button>
              <div className="grid gap-2">
                <div className="min-w-0">
                  <b className="block truncate text-[15px] font-semibold leading-tight">{s.pet_name}</b>
                  <span className="text-[13px] text-muted">{s.manager.display_name}</span>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <Pips filled={mine} popLast />
                  <Stepper value={mine} canAdd={left > 0 && mine < cap} onChange={(n) => change(s.id, n)} label={s.pet_name} />
                </div>
              </div>
            </li>
          )
        })}
      </ul>
      {next ? (
        <Button variant="ink" block onClick={next.go}>Next: {next.name}</Button>
      ) : (
        <p className="text-center text-[14px] text-muted">That's every category. Change your mind any time before voting closes.</p>
      )}
      <SubmissionSheet submission={open} canDelete={false} onClose={() => setOpen(null)} />
    </>
  )
}
